import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, ChevronDown, TriangleAlert } from "lucide-react";
import { maskDateInput, dateBrToIso, isoToDateBr } from "@/lib/dateTimeMask";
import { calcularPrazo, sugerirDias, PRAZO_PADRAO_DIAS } from "@/lib/prazoPadrao";

// Lote 110 — calculadora recolhida dentro do Novo evento (tipo Prazo). Se o evento vem de uma publicação
// (campo "Data da publicação" preenchido), parte da data de DISPONIBILIZAÇÃO e sugere os dias achados no
// texto; sem sugestão, avisa e pede os dias (padrão: 5 úteis). O resultado entra no agendamento (18:00).
export default function PrazoCalculadora({ description, publishedAt, cienciaBr, onUse }) {
  const [open, setOpen] = useState(false);
  const [base, setBase] = useState("disp"); // "disp" | "ciencia"
  const [baseDate, setBaseDate] = useState("");
  const [dias, setDias] = useState(String(PRAZO_PADRAO_DIAS));
  const [regime, setRegime] = useState("uteis");
  const [recesso, setRecesso] = useState(false);
  const [res, setRes] = useState(null);
  const [erro, setErro] = useState("");
  const reqId = useRef(0);
  const sugestoes = useMemo(() => sugerirDias(description), [description]);

  const abrir = () => {
    if (!open) {
      const temPub = Boolean(dateBrToIso(publishedAt));
      setBase(temPub ? "disp" : "ciencia");
      setBaseDate(temPub ? publishedAt : cienciaBr);
      const s = sugestoes[0];
      setDias(String(s ? s.dias : PRAZO_PADRAO_DIAS));
      setRegime(s?.regime || "uteis");
    }
    setOpen(!open);
  };

  useEffect(() => {
    if (!open) return undefined;
    const iso = dateBrToIso(baseDate);
    const n = parseInt(dias, 10);
    if (!iso || !n || n < 1) { setRes(null); setErro(""); return undefined; }
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const d = await calcularPrazo({ dataIso: iso, dias: n, regime, base: base === "disp" ? "disponibilizacao" : "ciencia", recesso });
        if (id === reqId.current) { setRes(d); setErro(""); }
      } catch (e) {
        if (id === reqId.current) { setRes(null); setErro(e?.response?.data?.detail || "Não consegui calcular agora"); }
      }
    }, 250);
    return () => clearTimeout(t);
  }, [open, base, baseDate, dias, regime, recesso]);

  const chip = (on) => `px-2.5 h-8 rounded-md border text-xs font-medium ${on ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`;
  const escolherBase = (b) => {
    setBase(b);
    if (b === "disp" && dateBrToIso(publishedAt)) setBaseDate(publishedAt);
    if (b === "ciencia") setBaseDate(cienciaBr);
  };

  return (
    <div data-testid="prazo-calc">
      <button
        type="button" data-testid="prazo-calc-toggle" onClick={abrir} aria-expanded={open}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#8cc7f5] bg-[#f3f9ff] text-xs font-semibold text-[#0b4f94] hover:border-[#0b4f94]"
      >
        <Calculator className="w-3.5 h-3.5" strokeWidth={1.75} /> Calculadora de prazo
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-stone-200 bg-white p-3 space-y-3" data-testid="prazo-calc-box">
          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Contar a partir de</Label>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" data-testid="prazo-calc-base-disp" onClick={() => escolherBase("disp")} className={chip(base === "disp")}>Disponibilização</button>
              <button type="button" data-testid="prazo-calc-base-ciencia" onClick={() => escolherBase("ciencia")} className={chip(base === "ciencia")}>Ciência</button>
              <Input
                data-testid="prazo-calc-base-date" inputMode="numeric" placeholder="dd/mm/aaaa" maxLength={10}
                value={baseDate} onChange={(e) => setBaseDate(maskDateInput(e.target.value))} className="h-8 w-32 text-sm"
              />
            </div>
            {base === "disp" && !dateBrToIso(baseDate) && (
              <p className="text-[11px] text-stone-500 mt-1">Informe a data em que a intimação foi disponibilizada no diário.</p>
            )}
          </div>

          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Dias do prazo</Label>
            {sugestoes.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="prazo-calc-sugestoes">
                <span className="text-[11px] text-stone-500 self-center">Achei no texto:</span>
                {sugestoes.map((s, i) => (
                  <button
                    key={`${s.dias}-${s.regime}`} type="button" data-testid={`prazo-calc-sugestao-${i}`} title={`…${s.trecho}`}
                    onClick={() => { setDias(String(s.dias)); if (s.regime) setRegime(s.regime); }}
                    className="px-2 h-7 rounded-full border border-[#8cc7f5] bg-[#f3f9ff] text-[11px] font-semibold text-[#0b4f94]"
                  >
                    {s.dias} {s.regime === "corridos" ? "corridos" : s.regime === "uteis" ? "úteis" : "dias"}
                  </button>
                ))}
              </div>
            ) : (
              <p className="flex items-start gap-1.5 text-[11px] text-amber-700 mb-2" data-testid="prazo-calc-aviso">
                <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-px" strokeWidth={2} />
                Não achei o número de dias no texto — informe os dias abaixo (padrão: {PRAZO_PADRAO_DIAS} úteis).
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Input
                data-testid="prazo-calc-dias" inputMode="numeric" maxLength={3} value={dias} aria-label="Dias do prazo"
                onChange={(e) => setDias(e.target.value.replace(/\D/g, ""))} className="h-8 w-16 text-sm"
              />
              <button type="button" data-testid="prazo-calc-regime-uteis" onClick={() => setRegime("uteis")} className={chip(regime === "uteis")}>Úteis</button>
              <button type="button" data-testid="prazo-calc-regime-corridos" onClick={() => setRegime("corridos")} className={chip(regime === "corridos")}>Corridos</button>
            </div>
            <label className="mt-2 flex items-center gap-2 text-[11px] text-stone-600">
              <input type="checkbox" data-testid="prazo-calc-recesso" checked={recesso} onChange={(e) => setRecesso(e.target.checked)} className="h-3.5 w-3.5 accent-stone-900" />
              Não contar o recesso forense (20/12 a 20/01)
            </label>
          </div>

          {erro && <p className="text-[11px] text-red-700" data-testid="prazo-calc-erro">{erro}</p>}
          {res && (
            <div className="rounded-md bg-[#f3f9ff] border border-[#8cc7f5] p-2.5" data-testid="prazo-calc-resultado">
              <div className="text-sm font-semibold text-stone-900">Vence {res.weekday_final}, {isoToDateBr(res.data_final)}</div>
              <p className="text-[11px] text-stone-500 mt-0.5">{res.resumo}. Só feriados nacionais (estaduais e municipais não entram).</p>
              <Button
                type="button" size="sm" data-testid="prazo-calc-usar" className="mt-2 h-9 bg-stone-900 text-white hover:bg-stone-800"
                onClick={() => { onUse?.(res.data_final, res.resumo); setOpen(false); }}
              >
                Usar no agendamento
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
